import React, { useState } from 'react';
import {
  Activity,
  AlertCircle,
  Mail,
  ChevronDown,
  ChevronUp,
  RefreshCw,
} from 'lucide-react';
import { useLogs } from '../../../hooks/useLogs';
import { useQueryClient } from '@tanstack/react-query';
import { formatRelativeTime } from '../../../utils/formatters';

const STATUS_BADGE = {
  sent:       { className: 'queue-status-badge queue-status-sent',       label: 'Sent' },
  delivered:  { className: 'queue-status-badge queue-status-sent',       label: 'Delivered' },
  opened:     { className: 'queue-status-badge queue-status-sent',       label: 'Opened' },
  failed:     { className: 'queue-status-badge queue-status-failed',     label: 'Failed' },
  bounced:    { className: 'queue-status-badge queue-status-failed',     label: 'Bounced' },
  spam:       { className: 'queue-status-badge queue-status-failed',     label: 'Spam' },
  blocked:    { className: 'queue-status-badge queue-status-processing', label: 'Blocked' },
  dropped:    { className: 'queue-status-badge queue-status-processing', label: 'Dropped' },
  deferred:   { className: 'queue-status-badge queue-status-queued',     label: 'Deferred' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_BADGE[status] || { className: 'queue-status-badge', label: status };
  return <span className={cfg.className}>{cfg.label}</span>;
}

const LIMIT = 25;

function EmailLogsPage() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchFilter, setSearchFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState(null);

  const filters = { page, limit: LIMIT };
  if (statusFilter !== 'All') filters.status = statusFilter;
  if (searchFilter)           filters.search = searchFilter;
  if (fromDate)               filters.from = fromDate;
  if (toDate)                 filters.to = toDate;

  const { data, isLoading, error } = useLogs(filters);
  const logs = data?.data || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  function handleFilterChange(setter) {
    return (e) => {
      setter(e.target.value);
      setPage(1);
    };
  }

  function handleRefresh() {
    queryClient.invalidateQueries({ queryKey: ['email-logs'] });
  }

  function toggleExpand(id) {
    setExpandedId((prev) => (prev === id ? null : id));
  }

  return (
    <div className="email-page">
      <div className="email-page-header">
        <h3>
          <Activity size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          Email Logs
        </h3>
        <button className="queue-action-btn" onClick={handleRefresh} title="Refresh">
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Filters */}
      <div className="email-filter-bar">
        <select
          className="email-filter-select"
          value={statusFilter}
          onChange={handleFilterChange(setStatusFilter)}
        >
          <option value="All">All Statuses</option>
          <option value="sent">Sent</option>
          <option value="delivered">Delivered</option>
          <option value="opened">Opened</option>
          <option value="failed">Failed</option>
          <option value="bounced">Bounced</option>
          <option value="blocked">Blocked</option>
          <option value="spam">Spam</option>
          <option value="deferred">Deferred</option>
        </select>

        <input
          className="email-filter-input"
          type="text"
          placeholder="Search recipient..."
          value={searchFilter}
          onChange={handleFilterChange(setSearchFilter)}
        />

        <input
          className="email-filter-input"
          type="date"
          value={fromDate}
          onChange={handleFilterChange(setFromDate)}
          title="From date"
        />
        <input
          className="email-filter-input"
          type="date"
          value={toDate}
          onChange={handleFilterChange(setToDate)}
          title="To date"
        />
      </div>

      {/* Error */}
      {error && (
        <div className="email-error">
          <AlertCircle size={16} />
          Failed to load logs: {error.message}
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="email-table-skeleton">
          {[...Array(8)].map((_, i) => <div key={i} className="skeleton-row" />)}
        </div>
      ) : logs.length === 0 ? (
        <div className="email-empty-state">
          <Mail size={40} />
          <p>No logs found</p>
        </div>
      ) : (
        <div className="email-table-wrapper">
          <table className="email-table">
            <thead>
              <tr>
                <th>Recipient</th>
                <th>Subject</th>
                <th>Template</th>
                <th>Status</th>
                <th>Sent</th>
                <th style={{ width: 36 }} />
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <React.Fragment key={log.id}>
                  <tr
                    className="email-table-row"
                    onClick={() => toggleExpand(log.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="email-table-name">{log.recipient}</td>
                    <td className="email-table-subject" title={log.subject}>
                      {log.subject || <span style={{ color: 'var(--color-text-muted)' }}>—</span>}
                    </td>
                    <td className="email-table-date">
                      {log.template_name || <span style={{ color: 'var(--color-text-muted)' }}>—</span>}
                    </td>
                    <td><StatusBadge status={log.status} /></td>
                    <td className="email-table-date">
                      {log.sent_at ? formatRelativeTime(log.sent_at) : '—'}
                    </td>
                    <td>
                      {expandedId === log.id
                        ? <ChevronUp size={15} style={{ color: 'var(--color-text-muted)' }} />
                        : <ChevronDown size={15} style={{ color: 'var(--color-text-muted)' }} />
                      }
                    </td>
                  </tr>

                  {expandedId === log.id && (
                    <tr>
                      <td colSpan={6} style={{ padding: 0 }}>
                        <div className="drawer-body" style={{ padding: '1rem 1.25rem', background: 'var(--color-bg-light)', borderBottom: '1px solid var(--color-border-light)' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem 2rem' }}>
                            <LogField label="Log ID" value={log.id} mono />
                            <LogField label="Job ID" value={log.job_id} mono />
                            <LogField label="Provider Message ID" value={log.provider_message_id || '—'} mono />
                            <LogField label="Created" value={log.created_at ? new Date(log.created_at).toLocaleString() : '—'} />
                            {log.delivered_at && <LogField label="Delivered" value={new Date(log.delivered_at).toLocaleString()} />}
                            {log.opened_at && <LogField label="Opened" value={new Date(log.opened_at).toLocaleString()} />}
                            {log.bounced && <LogField label="Bounce Reason" value={log.bounce_reason || '—'} />}
                            {log.error && (
                              <div style={{ gridColumn: '1 / -1' }}>
                                <div className="drawer-field-label">Error</div>
                                <div className="drawer-error-box" style={{ marginTop: 4 }}>
                                  <span className="drawer-error-message">{log.error}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!isLoading && total > LIMIT && (
        <div className="queue-pagination">
          <button
            className="queue-pagination-btn"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </button>
          <span className="queue-pagination-info">
            Page {page} of {totalPages} ({total} total)
          </span>
          <button
            className="queue-pagination-btn"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

function LogField({ label, value, mono }) {
  return (
    <div className="drawer-field">
      <div className="drawer-field-label">{label}</div>
      <div className="drawer-field-value" style={mono ? { fontFamily: 'monospace', fontSize: '0.8rem' } : {}}>
        {value}
      </div>
    </div>
  );
}

export default EmailLogsPage;
